import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { NewTabLink } from "components/NewTabLink";
import { PhoneNumberField } from "components/PhoneNumberField";

import {
  TERMS_OF_SERVICE_URL,
  WORK_PHONE_EXTENSION_MAX_LENGTH,
} from "../../../../constants";
import { PasswordToggableIconEndAdornment } from "../PasswordToggableIconEndAdornment";

import type { EmailAndPasswordFormErrorState } from "../../types";
import type { SignUpData } from "../SignUp/types";

interface Props {
  signUpRole: Extract<RoleValue, "CARRIER" | "SHIPPER">;
  shipperCompanyName: string | null;
  showPassword: boolean;
  workPhoneNumber: string;
  workPhoneNumberExtension: string;
  cellPhoneNumber: string;
  onFormChange: (key: keyof Omit<SignUpData, "persona">, value: string) => void;
  onShowPasswordChange: () => void;
  onSignup: VoidFunction;
  onTermsChange: (newValue: boolean) => void;
  formError: EmailAndPasswordFormErrorState;
  signupDisabled: boolean;
  signupError: UIError;
  loading: boolean;
}

export function ExternalInviteSignUp(props: Props) {
  const {
    signUpRole,
    shipperCompanyName,
    onFormChange,
    onShowPasswordChange,
    onSignup,
    onTermsChange,
    showPassword,
    workPhoneNumber,
    workPhoneNumberExtension,
    cellPhoneNumber,
    formError,
    signupDisabled,
    signupError,
    loading,
  } = props;

  const submitLabel =
    signUpRole === "CARRIER"
      ? "Create Carrier Account"
      : "Create Shipper Account";

  return (
    <Box>
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        {shipperCompanyName
          ? `Create an account to work with ${shipperCompanyName}`
          : "Signup for Evotrux"}
      </Typography>
      <Stack my="2rem" spacing={2}>
        <TextField
          id="email-input"
          label="Email"
          variant="outlined"
          onChange={(e) => onFormChange("email", e.target.value)}
          error={formError.email != null}
          helperText={formError.email ?? ""}
          autoFocus
        />
        <TextField
          id="confirm-email-input"
          label="Confirm Email"
          variant="outlined"
          onChange={(e) => onFormChange("confirmEmail", e.target.value)}
        />
        <TextField
          id="password-input"
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          onChange={(e) => onFormChange("password", e.target.value)}
          error={formError.password != null}
          helperText={formError.password ?? ""}
          InputProps={{
            endAdornment: (
              <PasswordToggableIconEndAdornment
                showPassword={showPassword}
                onIconClick={onShowPasswordChange}
              />
            ),
          }}
        />
        <TextField
          id="first-name-input"
          label="First name"
          variant="outlined"
          onChange={(e) => onFormChange("firstName", e.target.value)}
        />
        <TextField
          id="last-name-input"
          label="Family name"
          variant="outlined"
          onChange={(e) => onFormChange("lastName", e.target.value)}
        />
        <PhoneNumberField
          label="Work Phone Number"
          value={workPhoneNumber}
          onChange={(e) => onFormChange("workPhoneNumber", e)}
          fullWidth
        />
        <TextField
          id="work-phone-extension-input"
          label="Extension (optional)"
          variant="outlined"
          value={workPhoneNumberExtension}
          onChange={(e) =>
            onFormChange("workPhoneNumberExtension", e.target.value)
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: WORK_PHONE_EXTENSION_MAX_LENGTH,
          }}
        />
        <PhoneNumberField
          label="Cell Phone Number (optional)"
          value={cellPhoneNumber}
          onChange={(e) => onFormChange("cellPhoneNumber", e)}
          fullWidth
        />

        <FormControl required>
          <FormControlLabel
            control={
              <Checkbox onChange={(e) => onTermsChange(e.target.checked)} />
            }
            label={
              <>
                <span>I agree to the </span>
                <NewTabLink href={TERMS_OF_SERVICE_URL}>
                  <p>{" terms of service."}</p>
                </NewTabLink>
              </>
            }
          />
        </FormControl>
        <ErrorTypography error={signupError} />
        <LoadingWrappedNode loading={loading}>
          <Stack direction="column">
            <Button
              disabled={signupDisabled}
              variant="contained"
              onClick={onSignup}
            >
              {submitLabel}
            </Button>
          </Stack>
        </LoadingWrappedNode>
      </Stack>
    </Box>
  );
}
